import Phaser from 'phaser';
import { GAME_FONT_FAMILY, BOSS_COLOR } from '@shared/constants';
import { getKingdomLabel } from '@shared/data/kingdoms';
import { getNPCTemplate, buildNpcDisplayInfo, type NpcDisplayInfo } from '@shared/data/npcs';
import { formatStats, formatSpells, formatLoot } from '../NpcInfoPanel';
import { createNpcPortrait } from './npcPortrait';

/**
 * Tarjeta flotante con el detalle de un NPC dentro del World Map: mini-retrato +
 * nombre + Reino/boss + stats + hechizos + tabla de botín. Reutiliza
 * `buildNpcDisplayInfo` (dato shared) y los formatters de `NpcInfoPanel`, más el
 * helper de retrato `createNpcPortrait`. Es Phaser (vive sobre el canvas del
 * mapa), con `scrollFactor 0` y depth alto. Ver docs/WORLD_MAP.md.
 */

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

// Tipografía al DOBLE (pedido de Dalmiro): la tarjeta se ensancha y el retrato crece a la par.
const CARD_W = 500;
const PAD = 18;
const PORTRAIT = 96;

export class NpcDetailCard {
  private readonly scene: Phaser.Scene;
  private readonly depth: number;
  private container: Phaser.GameObjects.Container | null = null;

  constructor(scene: Phaser.Scene, depth: number) {
    this.scene = scene;
    this.depth = depth;
  }

  isVisible(): boolean {
    return this.container !== null;
  }

  /** Muestra la tarjeta del template dado, anclada cerca de (screenX, screenY). */
  showTemplate(templateId: string, screenX: number, screenY: number): void {
    const tpl = getNPCTemplate(templateId);
    if (!tpl) return;
    const info = buildNpcDisplayInfo(templateId, templateId, tpl.name);
    if (!info) return;
    this.render(info, screenX, screenY);
  }

  hide(): void {
    this.container?.destroy(true);
    this.container = null;
  }

  private render(info: NpcDisplayInfo, screenX: number, screenY: number): void {
    this.hide();
    const scene = this.scene;
    const tpl = getNPCTemplate(info.templateId);
    const kingdom = tpl?.kingdom ? getKingdomLabel(tpl.kingdom) : null;

    // Textos (medidos para calcular alto).
    const parts: Phaser.GameObjects.Text[] = [];
    const contentX = PAD + PORTRAIT + 14; // deja lugar al retrato a la izquierda
    let y = PAD;

    const title = scene.add
      .text(contentX, y, info.name, withGameFont({ fontSize: '28px', color: '#ffe9a8', fontStyle: 'bold', wordWrap: { width: CARD_W - contentX - PAD } }))
      .setScrollFactor(0);
    parts.push(title);
    y += title.height + 4;

    const sub = [kingdom, info.isBoss ? '★ BOSS' : null].filter(Boolean).join('  ·  ');
    if (sub) {
      const subT = scene.add
        .text(contentX, y, sub, withGameFont({ fontSize: '20px', color: info.isBoss ? BOSS_COLOR : '#b3a98c' }))
        .setScrollFactor(0);
      parts.push(subT);
      y += subT.height + 6;
    }
    // Bajo el retrato: arrancar el cuerpo debajo del max(y, retrato).
    y = Math.max(y, PAD + PORTRAIT + 8);

    const mkSection = (header: string, body: string, headerColor: string): void => {
      const h = scene.add.text(PAD, y, header, withGameFont({ fontSize: '20px', color: headerColor, fontStyle: 'bold' })).setScrollFactor(0);
      parts.push(h);
      y += h.height + 4;
      const b = scene.add
        .text(PAD, y, body, withGameFont({ fontSize: '22px', color: '#e8e0cc', wordWrap: { width: CARD_W - PAD * 2 }, lineSpacing: 3 }))
        .setScrollFactor(0);
      parts.push(b);
      y += b.height + 12;
    };
    // Sólo los NPCs hostiles (mobs) muestran la info de combate: a un neutral (comerciante,
    // quest giver, etc.) no le interesa HP/evasión/EXP/daño/def ni botín. Para neutrales la
    // tarjeta queda con retrato + nombre + Reino, y su alto (cardH) se achica solo.
    const hostile = tpl?.hostile === true;
    if (hostile) {
      mkSection('STATS', formatStats(info), '#88ccff');
      if (info.spells.length > 0) mkSection('HECHIZOS', formatSpells(info), '#cc99ff');
      mkSection('TABLA DE BOTÍN', formatLoot(info), '#ffcc66');
    }

    const cardH = y + PAD - 8;

    // Posición: mantener dentro de pantalla.
    const { width: sw, height: sh } = scene.scale;
    const px = Phaser.Math.Clamp(screenX + 16, 8, sw - CARD_W - 8);
    const py = Phaser.Math.Clamp(screenY - cardH / 2, 8, sh - cardH - 8);

    const container = scene.add.container(px, py).setScrollFactor(0).setDepth(this.depth);
    const bg = scene.add
      .rectangle(0, 0, CARD_W, cardH, 0x14130f, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0x8a7a4a, 1)
      .setScrollFactor(0)
      .setInteractive();
    // Absorber clicks (que no lleguen al mapa detrás).
    bg.on('pointerdown', (p: Phaser.Input.Pointer) => p.event.stopPropagation());
    container.add(bg);

    // Retrato (arriba-izquierda).
    const portrait = createNpcPortrait(scene, info.templateId, PAD + PORTRAIT / 2, PAD + PORTRAIT / 2, PORTRAIT);
    if (portrait) container.add(portrait);

    for (const t of parts) container.add(t);

    // Botón cerrar.
    const close = scene.add
      .text(CARD_W - 18, 12, '✕', withGameFont({ fontSize: '26px', color: '#ff6666' }))
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    close.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.hide();
      p.event.stopPropagation();
    });
    container.add(close);

    this.container = container;
  }
}
